import { h, onMounted, onUnmounted, ref } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import { completeWechatEmbeddedLogin } from "./login-options";
import GeneratePage from "./pages/GeneratePage.vue";
import LegalDocumentPage from "./pages/LegalDocumentPage.vue";
import LoginPage from "./pages/LoginPage.vue";
import { runtimeConfig } from "./runtime-config";
import { applyRouteSeo } from "./seo";
import { useAuthStore } from "./stores/auth";

const publicPaths = new Set(["/login", "/privacy", "/terms", "/wechat-login-bridge"]);

const WechatLoginBridge = {
  setup() {
    const auth = useAuthStore();
    const message = ref("正在登录");

    onMounted(() => {
      document.documentElement.classList.add("wechat-login-bridge-page");
      document.body.classList.add("wechat-login-bridge-page");
      void completeLogin();
    });
    onUnmounted(() => {
      document.documentElement.classList.remove("wechat-login-bridge-page");
      document.body.classList.remove("wechat-login-bridge-page");
    });

    async function completeLogin() {
      const params = new URLSearchParams(window.location.search);
      const existingToken = params.get("externalToken")?.trim() ?? "";
      if (existingToken) {
        auth.acceptExternalToken(existingToken);
        message.value = "登录成功";
        return;
      }

      const code = params.get("code")?.trim() ?? "";
      const state = params.get("state")?.trim() ?? "";
      if (!code) {
        message.value = "正在等待微信确认";
        return;
      }

      try {
        const login = await completeWechatEmbeddedLogin(code, state);
        auth.acceptExternalToken(login.token);
        message.value = "登录成功";
        if (window.self === window.top) {
          window.location.replace(runtimeConfig.market.generatePath);
        }
      } catch (error) {
        message.value = error instanceof Error ? error.message : "登录失败，请重试";
      }
    }

    return () => h(
      "main",
      { class: "wechat-login-bridge" },
      [
        h("div", { class: "wechat-login-bridge__loading", "aria-hidden": "true" }),
        h("p", message.value)
      ]
    );
  }
};

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/",
      redirect: "/login"
    },
    {
      path: "/login",
      component: LoginPage
    },
    {
      path: "/wechat-login-bridge",
      component: WechatLoginBridge
    },
    {
      path: "/privacy",
      component: LegalDocumentPage
    },
    {
      path: "/terms",
      component: LegalDocumentPage
    },
    {
      path: "/generate",
      component: GeneratePage
    },
  ]
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  const externalToken = typeof to.query.externalToken === "string" ? to.query.externalToken : "";
  if (externalToken) {
    auth.acceptExternalToken(externalToken);
    const restored = await auth.restoreSession();
    const isWechatBridgeFrame = typeof window !== "undefined"
      && window.self !== window.top
      && to.path === "/wechat-login-bridge";
    if (restored && isWechatBridgeFrame) {
      return true;
    }
    if (restored) {
      return {
        path: runtimeConfig.market.generatePath,
        query: {}
      };
    }
  }
  if (!auth.isLoggedIn && auth.hasStoredToken) {
    await auth.restoreSession();
  }
  if (!publicPaths.has(to.path) && !auth.isLoggedIn) {
    return runtimeConfig.market.loginPath;
  }
  if (to.path === "/login" && auth.isLoggedIn) {
    return runtimeConfig.market.generatePath;
  }
  return true;
});

router.afterEach((to) => {
  applyRouteSeo(to);
});
